import type {
  LoaderArgs,
  ActionArgs,
  V2_MetaFunction,
  HeadersFunction,
} from "@remix-run/cloudflare";
import { json } from "@remix-run/cloudflare";
import type { Env } from "./_index";
import { getAuth } from "@clerk/remix/ssr.server";
import { jobs } from "~/db/schema/jobs";
import { createDb } from "~/db/db-client.server";
import { and, eq, sql } from "drizzle-orm";
import type { Job, School } from "~/db/types";
import {
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import { isNumeric } from "~/utils/utils";
import { redirectWithToast } from "~/utils/toast.server";
import { JobPage } from "~/components/jobPage";
import { TypographyP } from "~/components/ui/typography/p";
import { TypographyH2 } from "~/components/ui/typography/h2";
import { schools } from "~/db/schema/schools";
import { savedjobs } from "~/db/schema/savedjobs";
import { candidates } from "~/db/schema/candidates";
import { appliedjobs } from "~/db/schema/appliedjobs";

export const meta: V2_MetaFunction<typeof loader> = ({ data }) => {
  const { job } = data;
  return [
    {
      title: `${job.title} in ${job.location} with ${job.schoolName} | SchoolJobs.live`,
    },
    { name: "description", content: "Welcome to Schooljobs.live" },
    {
      property: "og:url",
      content: `https://schooljobs.live/job/${job.id}`,
    },
    {
      property: "og:type",
      content: "article",
    },
    {
      property: "og:image",
      content: "https://schooljobs.live/images/banner.png",
    },
    {
      property: "og:title",
      content: `${job.title} in ${job.location} with ${job.schoolName} | SchoolJobs.live`,
    },
    {
      property: "og:description",
      content: `${job.shortDescription.replace(/<[^>]*>?/gm, "")}`,
    },
    {
      property: "twitter:card",
      content: "summary_large_image",
    },
    {
      property: "twitter:domain",
      content: "schooljobs.live",
    },
    {
      property: "twitter:url",
      content: `https://schooljobs.live/job/${job.id}`,
    },
    {
      property: "twitter:title",
      content: `${job.title} in ${job.location} with ${job.schoolName} | SchoolJobs.live`,
    },
    {
      property: "twitter:description",
      content: `${job.shortDescription.replace(/<[^>]*>?/gm, "")}`,
    },
    {
      property: "twitter:image",
      content: "https://schooljobs.live/images/banner.png",
    },
  ];
};

export const headers: HeadersFunction = () => ({
  "Cache-Control": "max-age=1, stale-while-revalidate=59",
});

export async function action({ request, params, context }: ActionArgs) {
  const { id } = params;
  if (!id || !isNumeric(id)) {
    return new Response("ID of the job is required", { status: 422 });
  }

  const { userId } = await getAuth(
    { request, context, params },
    {
      secretKey:
        context.env && (context.env as Env)["CLERK_SECRET_KEY"]
          ? (context.env as Env)["CLERK_SECRET_KEY"]
          : "",
    }
  );

  if (!userId) {
    return redirectWithToast("../sign-in", {
      title: "Sign In",
      description: "You should sign in to save/apply this job",
      type: "message",
    });
  }

  const db = createDb(context);

  const activeCandidateData = await db
    .select()
    .from(candidates)
    .where(eq(candidates.clerkUserId, userId))
    .get();

  if (!activeCandidateData) {
    return redirectWithToast("../candidate/profile", {
      title: "Please complete your profile",
      description: "Please complete your profile to save/apply this job",
      type: "message",
    });
  }

  if (request.method === "DELETE") {
    const job = await db
      .select()
      .from(jobs)
      .where(eq(jobs.id, Number(id)))
      .get();

    if (!job) {
      return new Response("Not found", { status: 404 });
    }
    if (job.clerkUserId !== userId) {
      return new Response("Unauthorized", { status: 401 });
    }

    const deletedJob = await db
      .delete(jobs)
      .where(eq(jobs.id, Number(id)))
      .returning()
      .get();

    return redirectWithToast("../recruiter/manage-your-jobs", {
      title: "Job Deleted",
      description: `${deletedJob?.title} has been deleted`,
      type: "message",
    });
  } else if (request.method === "POST") {
    const candidateId = activeCandidateData.id;

    const formData = await request.formData();

    try {
      if (formData.get("_intent") === "save") {
        if (!activeCandidateData) {
          return redirectWithToast("../candidate/profile", {
            title: "Please complete your profile",
            description: "Please complete your profile to save this job",
            type: "message",
          });
        }
        const savedJob = await db
          .insert(savedjobs)
          .values({ jobId: Number(id), candidateId })
          .returning()
          .get();
        console.log("Job inserted into saved jobs", savedJob);
        return { savedJob };
      } else if (formData.get("_intent") === "remove") {
        const deletedJob = await db
          .delete(savedjobs)
          .where(eq(savedjobs.jobId, Number(id)))
          .returning()
          .get();
        console.log("Job removed from saved jobs", deletedJob);
        return { deletedJob };
      } else if (formData.get("_intent") === "apply") {
        if (!activeCandidateData) {
          return redirectWithToast("../candidate/profile", {
            title: "Please complete your profile",
            description: "Please complete your profile to apply for this job",
            type: "message",
          });
        }
        const appliedJob = await db
          .insert(appliedjobs)
          .values({ jobId: Number(id), candidateId })
          .returning()
          .get();
        console.log("Job applied", appliedJob);
        return { appliedJob };
      }
    } catch (error) {
      console.log("Error saving or removing saved job into DB", error);
      return redirectWithToast(".", {
        title: "Uh oh! Something went wrong.",
        description: "There was a problem with your request. Please try again.",
        type: "error",
      });
    }
  }
}

export async function loader({ request, params, context }: LoaderArgs) {
  const { id } = params;

  const { userId } = await getAuth(
    { request, context, params },
    {
      secretKey:
        context.env && (context.env as Env)["CLERK_SECRET_KEY"]
          ? (context.env as Env)["CLERK_SECRET_KEY"]
          : "",
    }
  );

  if (!id) {
    throw new Response("ID of the job is required", { status: 422 });
  }

  if (!isNumeric(id)) {
    throw new Response("ID should be numeric", { status: 422 });
  }

  const db = createDb(context);

  let activeCandidateData;

  if (userId) {
    activeCandidateData = await db
      .select()
      .from(candidates)
      .where(eq(candidates.clerkUserId, userId))
      .get();
  }

  const job = await db
    .select({
      id: jobs.id,
      title: jobs.title,
      location: jobs.location,
      salary: jobs.salary,
      state: jobs.state,
      country: jobs.country,
      postalCode: jobs.postalCode,
      contractType: jobs.contractType,
      workingHours: jobs.workingHours,
      description: jobs.description,
      shortDescription: jobs.shortDescription,
      currency: jobs.currency,
      salaryTimePeriod: jobs.salaryTimePeriod,
      schoolId: jobs.schoolId,
      schoolName: jobs.schoolName,
      suspended: jobs.suspended,
      clerkUserId: jobs.clerkUserId,
      postedAt: jobs.postedAt,
      createdAt: jobs.createdAt,
      saved: sql<boolean>`${db
        .select({ n: savedjobs.createdAt })
        .from(savedjobs)
        .where(
          and(
            eq(savedjobs.jobId, jobs.id),
            eq(
              savedjobs.candidateId,
              activeCandidateData ? activeCandidateData.id : 0
            )
          )
        )}`,
      applied: sql<boolean>`${db
        .select({ n: appliedjobs.createdAt })
        .from(appliedjobs)
        .where(
          and(
            eq(appliedjobs.jobId, jobs.id),
            eq(
              appliedjobs.candidateId,
              activeCandidateData ? activeCandidateData.id : 0
            )
          )
        )}`,
    })
    .from(jobs)
    .where(eq(jobs.id, Number(id)))
    .get();

  const school = await db
    .select({
      name: schools.name,
      id: schools.id,
      logo: schools.logo,
    })
    .from(schools)
    .where(eq(schools.id, Number(job.schoolId)))
    .get();

  if (!job) {
    throw new Response("Not found", { status: 404 });
  }

  return json({ job, userId, school });
}

export default function JobAd() {
  const { job, userId, school } = useLoaderData() as {
    job: Job & { saved: null | number };
    userId: string;
    school: Partial<School>;
  };
  console.log(job);
  return job.suspended && job.clerkUserId !== userId ? (
    <>
      <TypographyH2>Job Suspended</TypographyH2>
      <TypographyP>
        This job has been suspended by the recruiter. No longer available.
      </TypographyP>
    </>
  ) : (
    <JobPage data={job} school={school} />
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  if (isRouteErrorResponse(error)) {
    return (
      <div>
        <h1>Oops</h1>
        <p>Status: {error.status}</p>
        <p>{error.data}</p>
      </div>
    );
  } else {
    return (
      <div>
        <h1>Uh oh ...</h1>
        <p>Something went wrong.</p>
        <pre>{error.data}</pre>
      </div>
    );
  }
}
